import useAutoRefresh from "@/hooks/useAutoRefresh";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Pill } from "@/components/perf";
import { CalendarPlus, Trash2, CalendarCheck, X, Pencil, CalendarX2 } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import CoachClientList from "@/components/CoachClientList";

const DRILL_TYPES = ["Dinks", "Drops", "Drives", "Resets", "Mixed", "Non Volley Zone", "Transition Zone", "Game Skills", "Competitive Drills"];
const EMPTY_SLOT = { date: "", times: [], drill_type: "Mixed", coach: "", capacity: 8, location: "", price: 0, price_coins: "", venue: "indoor", repeat_weekly: false, repeat_weeks: 8 };

const PRESET_TIMES = (() => {
  const out = [];
  for (let h = 6; h <= 21; h++) {
    for (const m of [0, 30]) {
      const ampm = h >= 12 ? "PM" : "AM";
      const hh = h % 12 === 0 ? 12 : h % 12;
      out.push(`${String(hh).padStart(2, "0")}:${m === 0 ? "00" : "30"} ${ampm}`);
    }
  }
  return out;
})();

export default function SlotManager() {
  const { user } = useAuth();
  const isCoachOnly = user?.role !== "admin";
  const [clientsFirst, setClientsFirst] = useState(() => localStorage.getItem("coach.console.clientsFirst") !== "0");
  const swapConsole = () => {
    const v = !clientsFirst;
    setClientsFirst(v);
    localStorage.setItem("coach.console.clientsFirst", v ? "1" : "0");
  };
  const [slots, setSlots] = useState([]);
  const [form, setForm] = useState(EMPTY_SLOT);
  const [savingSlot, setSavingSlot] = useState(false);
  const [editSlot, setEditSlot] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [coaches, setCoaches] = useState([]);

  const loadSlots = async () => {
    try {
      const { data } = await api.get("/admin/slots");
      setSlots(data);
    } catch (e) { /* ignore */ }
  };

  const loadCoaches = async () => {
    try {
      const { data } = await api.get("/coaches");
      setCoaches(Array.isArray(data) ? data : []);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { loadSlots(); loadCoaches(); }, []);
  useAutoRefresh(() => { loadSlots(); loadCoaches(); });

  const createSlot = async () => {
    const coachValue = isCoachOnly ? (user?.name || "Coach") : form.coach;
    if (!form.date || form.times.length === 0 || !coachValue || !form.location || !form.drill_type.trim()) return toast.error(form.times.length === 0 ? "Pick at least one time" : "Fill all fields");
    setSavingSlot(true);
    try {
      const { data } = await api.post("/admin/slots", {
        date: form.date, time: form.times[0], times: form.times, drill_type: form.drill_type, coach: coachValue,
        capacity: Number(form.capacity), location: form.location, price: Number(form.price) || 0,
        price_coins: Number(form.price_coins) || null,
        venue: form.venue,
        repeat_weekly: form.repeat_weekly, repeat_weeks: Number(form.repeat_weeks) || 1,
      });
      toast.success(data.created > 1 ? `${data.created} sessions added` : "Session slot added");
      setForm(EMPTY_SLOT);
      await loadSlots();
    } finally { setSavingSlot(false); }
  };

  const openEdit = (s) => setEditSlot({ id: s.id, date: s.date, time: s.time, drill_type: s.drill_type, coach: s.coach, capacity: s.capacity, location: s.location, price: s.price || 0, price_coins: s.price_coins || "", venue: s.venue || "indoor", series_id: s.series_id || null, apply_to_series: !!s.series_id });

  const saveEdit = async () => {
    if (!editSlot.date || !editSlot.time || !editSlot.coach || !editSlot.location || !editSlot.drill_type.trim()) return toast.error("Fill all fields");
    setSavingEdit(true);
    try {
      const { data } = await api.put(`/admin/slots/${editSlot.id}`, {
        date: editSlot.date, time: editSlot.time, drill_type: editSlot.drill_type,
        coach: editSlot.coach, capacity: Number(editSlot.capacity), location: editSlot.location,
        price: Number(editSlot.price) || 0, price_coins: Number(editSlot.price_coins) || null, venue: editSlot.venue,
        apply_to_series: !!(editSlot.series_id && editSlot.apply_to_series),
      });
      toast.success(data.updated_count > 1 ? `${data.updated_count} weekly sessions updated` : "Session updated");
      setEditSlot(null);
      await loadSlots();
    } finally { setSavingEdit(false); }
  };

  const deleteSlot = async (id) => {
    await api.delete(`/admin/slots/${id}`);
    toast.success("Slot removed");
    await loadSlots();
  };

  const deleteSeries = async (seriesId) => {
    if (!window.confirm("Delete the ENTIRE weekly series? Every session in this recurring block will be removed.")) return;
    const { data } = await api.delete(`/admin/slots/series/${seriesId}`);
    toast.success(`${data.deleted} weekly sessions removed`);
    await loadSlots();
  };

  const fmtDate = (d) => { try { return new Date(d + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }); } catch { return d; } };

  return (
    <div className="mt-8" data-testid="slot-manager">
      <h3 className="font-display font-bold text-2xl uppercase mb-1 flex items-center gap-2"><CalendarCheck className="w-6 h-6 text-pb-red" /> Session Availability</h3>
      <p className="text-sm text-zinc-500 mb-5">Create and regulate drill-session slots. Players book these from the Book tab.</p>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Create form */}
        <div className="bg-pb-surface border border-pb-border p-5">
          <div className="flex items-center gap-2 mb-4">
            <CalendarPlus className="w-4 h-4 text-pb-red" />
            <span className="font-display font-bold text-lg uppercase">New Slot</span>
          </div>
          <div className="space-y-3">
            <SlotField label="Date" type="date" value={form.date} onChange={(v) => setForm({ ...form, date: v })} testid="slot-date" />
            <TimeMultiPicker times={form.times} onChange={(times) => setForm({ ...form, times })} />
            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Session Type</label>
              <select data-testid="slot-drill" value={DRILL_TYPES.includes(form.drill_type) ? form.drill_type : "__custom"}
                onChange={(e) => setForm({ ...form, drill_type: e.target.value === "__custom" ? "" : e.target.value })}
                className="w-full bg-pb-elevated border border-pb-border px-3 py-2 text-white focus:outline-none focus:border-pb-red">
                {DRILL_TYPES.map((d) => <option key={d} value={d}>{d}</option>)}
                <option value="__custom">＋ Custom — name my own…</option>
              </select>
              {!DRILL_TYPES.includes(form.drill_type) && (
                <input data-testid="slot-drill-custom" value={form.drill_type} autoFocus
                  onChange={(e) => setForm({ ...form, drill_type: e.target.value })}
                  placeholder="Type your new session name"
                  className="mt-2 w-full bg-pb-elevated border border-pb-cyan/50 px-3 py-2 text-white placeholder-zinc-600 focus:outline-none focus:border-pb-cyan" />
              )}
            </div>
            {isCoachOnly ? (
              <div>
                <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Coach</label>
                <div data-testid="slot-coach-locked" className="w-full bg-pb-elevated border border-pb-border px-3 py-2 text-zinc-300">{user?.name} <span className="text-zinc-500 text-xs">(you)</span></div>
              </div>
            ) : (
              <CoachSelect coaches={coaches} value={form.coach} onChange={(v) => setForm({ ...form, coach: v })} testid="slot-coach" />
            )}
            <SlotField label="Capacity" type="number" value={form.capacity} onChange={(v) => setForm({ ...form, capacity: v })} testid="slot-capacity" />
            <SlotField label="Price ($ · 0 = free)" type="number" value={form.price} onChange={(v) => setForm({ ...form, price: v })} testid="slot-price" />
            <SlotField label="Coin Price (🪙 · blank = no coin pay)" type="number" value={form.price_coins} onChange={(v) => setForm({ ...form, price_coins: v })} testid="slot-price-coins" />
            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Indoor / Outdoor</label>
              <select data-testid="slot-venue" value={form.venue} onChange={(e) => setForm({ ...form, venue: e.target.value })}
                className="w-full bg-pb-elevated border border-pb-border px-3 py-2 text-white focus:outline-none focus:border-pb-red">
                <option value="indoor">Indoor</option>
                <option value="outdoor">Outdoor</option>
              </select>
            </div>
            <SlotField label="Location" value={form.location} onChange={(v) => setForm({ ...form, location: v })} placeholder="Bullpen Court A" testid="slot-location" />
            <div className="flex items-center justify-between py-1 border-t border-pb-border/50 pt-3">
              <div>
                <div className="text-sm font-semibold">Repeat weekly</div>
                <div className="text-[11px] text-zinc-500">Auto-create this session every week.</div>
              </div>
              <Switch data-testid="slot-repeat-toggle" checked={form.repeat_weekly}
                onCheckedChange={(v) => setForm({ ...form, repeat_weekly: v })}
                className="data-[state=checked]:bg-pb-red" />
            </div>
            {form.repeat_weekly && (
              <SlotField label="Number of weeks" type="number" value={form.repeat_weeks} onChange={(v) => setForm({ ...form, repeat_weeks: v })} testid="slot-repeat-weeks" />
            )}
            <button data-testid="create-slot-btn" onClick={createSlot} disabled={savingSlot}
              className="w-full bg-pb-red text-black font-bold uppercase tracking-widest py-3 hover:bg-[#BEF264] active:scale-95 transition-all disabled:opacity-50">
              {savingSlot ? "Adding…" : (() => {
                const n = form.times.length || 1;
                const total = n * (form.repeat_weekly ? Number(form.repeat_weeks) || 1 : 1);
                return total > 1 ? `Add ${total} Session Slots` : "Add Session Slot";
              })()}
            </button>
          </div>
        </div>

        {/* Client list + Slot list */}
        <div className="lg:col-span-2 space-y-6">
          {clientsFirst && <CoachClientList isCoachOnly={isCoachOnly} onSwap={swapConsole} />}
          <div className="bg-pb-surface border border-pb-border">
          <div className="flex items-center justify-between p-5 border-b border-pb-border">
            <span className="font-display font-bold text-lg uppercase">Scheduled Sessions ({slots.length})</span>
            <span className="text-xs text-zinc-500 font-mono">booked / capacity</span>
          </div>
          <div className="divide-y divide-pb-border/50 max-h-[460px] overflow-y-auto">
            {slots.length === 0 && <div className="p-5 text-sm text-zinc-600">No sessions scheduled yet.</div>}
            {slots.map((s) => (
              <div key={s.id} data-testid={`admin-slot-${s.id}`} className="grid grid-cols-[1fr_auto_auto] gap-3 items-center px-5 py-3 text-sm">
                <div>
                  <div className="font-semibold flex items-center gap-2 flex-wrap">
                    {fmtDate(s.date)} · {s.time}
                    <Pill color="#A3E635">{s.drill_type}</Pill>
                    {s.series_id && <Pill color="#22D3EE">Weekly</Pill>}
                  </div>
                  <div className="text-xs text-zinc-500">{s.coach} · {s.location} · {s.price > 0 ? `$${s.price}` : "Free"}{s.booked_names?.length ? ` · ${s.booked_names.join(", ")}` : ""}</div>
                </div>
                <span className={`font-mono ${s.booked >= s.capacity ? "text-red-400" : "text-pb-cyan"}`}>{s.booked}/{s.capacity}</span>
                <div className="flex items-center gap-2">
                  <button data-testid={`edit-slot-${s.id}`} onClick={() => openEdit(s)} title="Edit session"
                    className="w-8 h-8 flex items-center justify-center border border-pb-border hover:border-pb-red hover:text-pb-red transition-colors">
                    <Pencil className="w-4 h-4" />
                  </button>
                  {s.series_id && (
                    <button data-testid={`delete-series-${s.id}`} onClick={() => deleteSeries(s.series_id)} title="Delete entire weekly series"
                      className="w-8 h-8 flex items-center justify-center border border-pb-border hover:border-red-500 hover:text-red-400 transition-colors">
                      <CalendarX2 className="w-4 h-4" />
                    </button>
                  )}
                  <button data-testid={`delete-slot-${s.id}`} onClick={() => deleteSlot(s.id)} title="Delete this session"
                    className="w-8 h-8 flex items-center justify-center border border-pb-border hover:border-red-500 hover:text-red-400 transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
          </div>
          {!clientsFirst && <CoachClientList isCoachOnly={isCoachOnly} onSwap={swapConsole} />}
        </div>
      </div>

      {editSlot && (
        <div className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setEditSlot(null)}>
          <div className="bg-pb-surface border border-pb-red w-full max-w-md animate-fade-up" onClick={(e) => e.stopPropagation()} data-testid="edit-slot-modal">
            <div className="flex items-center justify-between p-5 border-b border-pb-border">
              <div className="flex items-center gap-2">
                <Pencil className="w-5 h-5 text-pb-red" />
                <h3 className="font-display font-black text-xl uppercase">Edit Session</h3>
              </div>
              <button data-testid="edit-slot-close" onClick={() => setEditSlot(null)} className="text-zinc-500 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-3">
              <SlotField label="Date" type="date" value={editSlot.date} onChange={(v) => setEditSlot({ ...editSlot, date: v })} testid="edit-slot-date" />
              <SlotField label="Time" value={editSlot.time} onChange={(v) => setEditSlot({ ...editSlot, time: v })} placeholder="06:30 PM" testid="edit-slot-time" />
              <div>
                <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Session Type</label>
                <select data-testid="edit-slot-drill" value={DRILL_TYPES.includes(editSlot.drill_type) ? editSlot.drill_type : "__custom"}
                  onChange={(e) => setEditSlot({ ...editSlot, drill_type: e.target.value === "__custom" ? "" : e.target.value })}
                  className="w-full bg-pb-elevated border border-pb-border px-3 py-2 text-white focus:outline-none focus:border-pb-red">
                  {DRILL_TYPES.map((d) => <option key={d} value={d}>{d}</option>)}
                  <option value="__custom">＋ Custom — name my own…</option>
                </select>
                {!DRILL_TYPES.includes(editSlot.drill_type) && (
                  <input data-testid="edit-slot-drill-custom" value={editSlot.drill_type}
                    onChange={(e) => setEditSlot({ ...editSlot, drill_type: e.target.value })}
                    placeholder="Type your new session name"
                    className="mt-2 w-full bg-pb-elevated border border-pb-cyan/50 px-3 py-2 text-white placeholder-zinc-600 focus:outline-none focus:border-pb-cyan" />
                )}
              </div>
              {!isCoachOnly && <CoachSelect coaches={coaches} value={editSlot.coach} onChange={(v) => setEditSlot({ ...editSlot, coach: v })} testid="edit-slot-coach" />}
              <SlotField label="Capacity" type="number" value={editSlot.capacity} onChange={(v) => setEditSlot({ ...editSlot, capacity: v })} testid="edit-slot-capacity" />
              <SlotField label="Price ($ · 0 = free)" type="number" value={editSlot.price} onChange={(v) => setEditSlot({ ...editSlot, price: v })} testid="edit-slot-price" />
              <SlotField label="Coin Price (🪙)" type="number" value={editSlot.price_coins} onChange={(v) => setEditSlot({ ...editSlot, price_coins: v })} testid="edit-slot-price-coins" />
              <div>
                <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Indoor / Outdoor</label>
                <select data-testid="edit-slot-venue" value={editSlot.venue} onChange={(e) => setEditSlot({ ...editSlot, venue: e.target.value })}
                  className="w-full bg-pb-elevated border border-pb-border px-3 py-2 text-white focus:outline-none focus:border-pb-red">
                  <option value="indoor">Indoor</option>
                  <option value="outdoor">Outdoor</option>
                </select>
              </div>
              <SlotField label="Location" value={editSlot.location} onChange={(v) => setEditSlot({ ...editSlot, location: v })} testid="edit-slot-location" />
              {editSlot.series_id && (
                <div className="border border-pb-cyan/40 bg-pb-cyan/5 p-3" data-testid="edit-slot-series-box">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-semibold text-pb-cyan">Apply to all weekly occurrences</div>
                      <div className="text-[11px] text-zinc-500 mt-0.5">Updates every session in this weekly series. A date change shifts each week by the same number of days.</div>
                    </div>
                    <Switch data-testid="edit-slot-series-toggle" checked={editSlot.apply_to_series}
                      onCheckedChange={(v) => setEditSlot({ ...editSlot, apply_to_series: v })}
                      className="data-[state=checked]:bg-pb-cyan" />
                  </div>
                </div>
              )}
              <button data-testid="save-slot-btn" onClick={saveEdit} disabled={savingEdit}
                className="w-full bg-pb-red text-black font-bold uppercase tracking-widest py-3 hover:bg-[#BEF264] active:scale-95 transition-all disabled:opacity-50">
                {savingEdit ? "Saving…" : editSlot.series_id && editSlot.apply_to_series ? "Save for All Weeks" : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TimeMultiPicker({ times, onChange }) {
  const [custom, setCustom] = useState("");
  const toggle = (t) => onChange(times.includes(t) ? times.filter((x) => x !== t) : [...times, t]);
  const addCustom = () => {
    const t = custom.trim();
    if (!t) return;
    if (!times.includes(t)) onChange([...times, t]);
    setCustom("");
  };
  return (
    <div data-testid="slot-times-picker">
      <div className="flex items-center justify-between mb-2">
        <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500">Times — tap all you want that day{times.length > 0 ? ` (${times.length})` : ""}</label>
        {times.length > 0 && (
          <button data-testid="slot-times-clear" onClick={() => onChange([])}
            className="text-[10px] uppercase tracking-widest text-zinc-500 hover:text-pb-red font-bold">Clear</button>
        )}
      </div>
      <div className="grid grid-cols-4 gap-1 max-h-40 overflow-y-auto pr-1">
        {PRESET_TIMES.map((t) => {
          const on = times.includes(t);
          return (
            <button key={t} data-testid={`time-chip-${t.replace(/[: ]/g, "")}`} onClick={() => toggle(t)} aria-pressed={on}
              className={`text-[11px] font-mono py-1.5 border transition-colors ${
                on ? "border-pb-red bg-pb-red text-black font-bold" : "border-pb-border text-zinc-400 hover:border-pb-red/60 hover:text-white"}`}>
              {t.replace(/^0/, "")}
            </button>
          );
        })}
      </div>
      <div className="flex gap-2 mt-2">
        <input data-testid="slot-time-custom" value={custom} onChange={(e) => setCustom(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && addCustom()}
          placeholder="Custom time e.g. 06:45 PM"
          className="flex-1 bg-pb-elevated border border-pb-border px-3 py-1.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-pb-red" />
        <button data-testid="slot-time-custom-add" onClick={addCustom}
          className="border border-pb-border px-3 text-xs uppercase tracking-widest text-zinc-400 hover:border-pb-red hover:text-pb-red font-bold">Add</button>
      </div>
      {times.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2" data-testid="slot-times-selected">
          {times.map((t) => (
            <span key={t} className="inline-flex items-center gap-1 bg-pb-red/10 border border-pb-red/50 text-pb-red text-[11px] font-mono px-2 py-0.5">
              {t}
              <button data-testid={`time-remove-${t.replace(/[: ]/g, "")}`} onClick={() => toggle(t)} className="hover:text-white font-bold">×</button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function CoachSelect({ coaches, value, onChange, testid }) {
  const names = coaches.map((c) => c.name);
  const isCustom = !names.includes(value);
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Coach</label>
      <select data-testid={testid} value={isCustom ? "__custom" : value}
        onChange={(e) => onChange(e.target.value === "__custom" ? "" : e.target.value)}
        className="w-full bg-pb-elevated border border-pb-border px-3 py-2 text-white focus:outline-none focus:border-pb-red">
        {names.map((n) => <option key={n} value={n}>{n}</option>)}
        <option value="__custom">＋ Other — type a name…</option>
      </select>
      {isCustom && (
        <input data-testid={`${testid}-custom`} value={value} onChange={(e) => onChange(e.target.value)}
          placeholder="Coach name"
          className="mt-2 w-full bg-pb-elevated border border-pb-cyan/50 px-3 py-2 text-white placeholder-zinc-600 focus:outline-none focus:border-pb-cyan" />
      )}
    </div>
  );
}

function SlotField({ label, value, onChange, type = "text", placeholder, testid }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">{label}</label>
      <input data-testid={testid} type={type} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)}
        className="w-full bg-pb-elevated border border-pb-border px-3 py-2 text-white placeholder-zinc-600 focus:outline-none focus:border-pb-red" />
    </div>
  );
}
